import { useEffect } from 'react'
import { useTranslation } from 'next-i18next'

import useSessionAndLoginRedirection from '~/auth/hooks/useSessionAndLoginRedirection'
import { EventAction, logEvent, LOG_IN } from '~/utils/analytics'
import { LoginType } from '~/analytics/account/accountEvents.types'
import { selectFields, setBackendErrorActionCreator } from '~/auth/slices/login.slice'
import { useAppSelector, useAppDispatch } from '~/redux/hooks'
import { trackingEventStore } from '~/behavior-tracking/utility/tracking-events-store'
import { useLoginUserWithEmailPasswordMutation } from '~/auth/apis/auth.api'
import { AuthenticationResponse, SuccessResult } from '~/redux/apis/types'

interface AuthenticationError {
  [key: string]: 'userDoesNotExist' | 'passwordIncorrect'
}

// todoo! update error response & transaltion
const ERROR_MAPPING: AuthenticationError = {
  userDoesNotExist: 'userDoesNotExist',
  passwordIncorrect: 'passwordIncorrect',
}

export default function useAuthenticateWithEmail(isSubscriptionCheckout = false): {
  handleAuthenticateWithEmail: (event: React.FormEvent) => Promise<void>
  isLoading: boolean
  isSuccess: boolean
  response?: SuccessResult<AuthenticationResponse>
} {
  const { t } = useTranslation('errors')
  const dispatch = useAppDispatch()
  const fields = useAppSelector(selectFields)
  const [loginUserLoginWithEmailPass, { isError, isSuccess, isLoading, data: response, error }] =
    useLoginUserWithEmailPasswordMutation()

  const setSessionAndLoginRedirection = useSessionAndLoginRedirection()

  useEffect(() => {
    if (isSuccess && response && !error) {
      setSessionAndLoginRedirection({
        result: response as SuccessResult<AuthenticationResponse>,
        loginType: LoginType.EMAIL,
        loginAfterSignup: false,
        isSubscriptionCheckout,
      })
    }

    if (isError && error) {
      if ('data' in error) {
        dispatch(setBackendErrorActionCreator(t(`${ERROR_MAPPING[error?.data.message] || 'unknown'}`)))
      } else {
        dispatch(setBackendErrorActionCreator(t('unknown')))
        logEvent(LOG_IN(EventAction.LOGIN_EMAIL, false))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, error, isError, isSuccess, response])

  async function handleAuthenticateWithEmail(event: React.FormEvent): Promise<void> {
    event.preventDefault()
    if (!isLoading) {
      dispatch(setBackendErrorActionCreator(''))
      const pseudoId = await trackingEventStore.getPseudoId()
      loginUserLoginWithEmailPass({ email: fields.email, password: fields.password, pseudoId })
    }
  }

  return {
    handleAuthenticateWithEmail,
    isLoading,
    isSuccess,
    response,
  }
}
